// src/Components/AIMito/ChatResponseActions.tsx
import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PdfIcon, SpeakerOnIcon, SpeakerOffIcon } from 'assets/icons';

interface ChatResponseActionsProps {
  messageId: string; // For screenshot
  message: string; // The AI’s text for TTS
}

const ChatResponseActions: React.FC<ChatResponseActionsProps> = ({
  messageId,
  message,
}) => {
  // State to track whether TTS is currently speaking
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // --- Download PDF function remains unchanged ---
  const downloadPDF = async () => {
    const responseElement = document.getElementById(messageId);
    if (!responseElement) return;

    const canvas = await html2canvas(responseElement, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 190; // Fit width within A4
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const date = new Date().toLocaleDateString();

    pdf.setFontSize(16);
    pdf.text('Accountants on Air LLC: AI Mito Report', 10, 15);
    pdf.setFontSize(12);
    pdf.text(`Date: ${date}`, 10, 25);
    pdf.line(10, 30, 200, 30);

    pdf.addImage(imgData, 'PNG', 10, 35, imgWidth, imgHeight);
    pdf.save('AI_Mito_Response.pdf');
  };

  // --- Toggle Text-To-Speech (TTS) ---
  const toggleTTS = (text: string) => {
    if (!('speechSynthesis' in window)) {
      alert("Your browser doesn't support text-to-speech!");
      return;
    }

    if (!isSpeaking) {
      // Start TTS
      const utterance = new SpeechSynthesisUtterance(text);
      // When TTS finishes, update the state
      utterance.onend = () => {
        setIsSpeaking(false);
      };
      utteranceRef.current = utterance;
      speechSynthesis.speak(utterance);
      setIsSpeaking(true);
    } else {
      // Stop TTS
      speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  return (
    <div
      className="chat-response-actions"
      style={{
        display: 'flex',
        flexDirection: 'column', // Arrange buttons vertically
        gap: '0.5rem',
        position: 'absolute',
        right: '0.5rem',
        bottom: '-2rem', // Adjust this value to position as desired
      }}
    >
      {/* PDF Download Button */}
      <button onClick={downloadPDF} className="action-btn" title="Download PDF">
        <PdfIcon />
      </button>

      {/* TTS Read Aloud Button */}
      <button
        onClick={() => toggleTTS(message)}
        className="action-btn"
        title={isSpeaking ? 'Stop Reading' : 'Read Aloud'}
      >
        {isSpeaking ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
      </button>
    </div>
  );
};

export default ChatResponseActions;
